import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';
import { adminRHCategories } from '../data/adminRHReferentiel';

interface AdminRHSpiderChartProps {
  className?: string;
}

export default function AdminRHSpiderChart({ className = "" }: AdminRHSpiderChartProps) {
  const chartData = adminRHCategories.map(category => ({
    category: category.name,
    ASIS: category.currentScore,
    Target: Math.min(95, category.minScore + 15),
    Minimum: category.minScore
  }));

  const asisAverage = Math.round(
    adminRHCategories.reduce((sum, c) => sum + c.currentScore, 0) / adminRHCategories.length
  );
  const targetAverage = Math.round(
    chartData.reduce((sum, d) => sum + d.Target, 0) / chartData.length
  );

  return (
    <div className={`bg-white rounded-lg p-6 shadow-md ${className}`}>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-bz-dark mb-2">
          📊 Profil Admin/RH Exemple
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Visualisation ASIS vs Target - État actuel vs objectifs d'excellence administrative
        </p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
            <div className="text-sm text-teal-600 font-medium">Score ASIS (Actuel)</div>
            <div className="text-2xl font-bold text-teal-700">{asisAverage}%</div>
            <div className="text-xs text-teal-500">État actuel</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-sm text-emerald-600 font-medium">Score Target (Objectif)</div>
            <div className="text-2xl font-bold text-emerald-700">{targetAverage}%</div>
            <div className="text-xs text-emerald-500">Excellence visée</div>
          </div>
        </div>
      </div>
      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#374151' }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#6b7280' }} tickCount={6} />
            <Radar name="ASIS (Actuel)" dataKey="ASIS" stroke="#0D9488" fill="#0D9488" fillOpacity={0.15} strokeWidth={3} dot={{ fill: '#0D9488', strokeWidth: 2, r: 4 }} />
            <Radar name="Target (Objectif)" dataKey="Target" stroke="#059669" fill="#059669" fillOpacity={0.05} strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#059669', strokeWidth: 2, r: 3 }} />
            <Radar name="Minimum (75%)" dataKey="Minimum" stroke="#E74C3C" fill="transparent" strokeWidth={1} strokeDasharray="2 2" dot={false} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">🎯 Points Forts (ASIS ≥ 85%)</h4>
          <div className="space-y-1">
            {chartData.filter(item => item.ASIS >= 85).map(item => (
              <div key={item.category} className="flex justify-between text-sm">
                <span className="text-gray-700">{item.category}</span>
                <span className="font-bold text-teal-600">{item.ASIS}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">📈 Axes d&apos;Amélioration (ASIS &lt; 80%)</h4>
          <div className="space-y-1">
            {chartData.filter(item => item.ASIS < 80).map(item => (
              <div key={item.category} className="flex justify-between text-sm">
                <span className="text-gray-700">{item.category}</span>
                <span className="font-bold text-amber-600">{item.ASIS}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>
          <span className="inline-block w-3 h-3 bg-teal-500 rounded-full mr-1"></span>
          <strong>ASIS</strong> = État actuel •
          <span className="inline-block w-3 h-3 bg-emerald-500 rounded-full mx-1"></span>
          <strong>Target</strong> = Objectif •
          <span className="inline-block w-3 h-3 bg-red-500 rounded-full mx-1"></span>
          <strong>Minimum</strong> = 75%
        </p>
      </div>
    </div>
  );
}
